import type { ObjectDirective } from 'vue';
import {
    isHtmlSanitizationPolicy,
    sanitizeHtml,
    type HtmlSanitizationPolicy,
} from '@/utils/sanitize';

export type SafeHtmlValue = string | null | undefined;
export type SafeHtmlDirective = ObjectDirective<
    HTMLElement,
    SafeHtmlValue,
    never,
    HtmlSanitizationPolicy
>;

const renderedPolicyByElement = new WeakMap<HTMLElement, string | undefined>();

declare module 'vue' {
    interface GlobalDirectives {
        vSafeHtml: SafeHtmlDirective;
    }
}

function renderSafeHtml(
    element: HTMLElement,
    value: SafeHtmlValue,
    policyName?: string,
): void {
    const html = typeof value === 'string' ? value : '';
    renderedPolicyByElement.set(element, policyName);
    element.innerHTML = isHtmlSanitizationPolicy(policyName)
        ? sanitizeHtml(html, policyName)
        : '';
}

/**
 * 按具名策略清洗绑定的原始值，并在挂载或更新后写入元素。
 * 缺失或未知策略会失败关闭，不提供跳过清洗的修饰符。
 */
export const safeHtmlDirective: SafeHtmlDirective = {
    mounted(element, binding) {
        renderSafeHtml(element, binding.value, binding.arg);
    },
    updated(element, binding) {
        const policyUnchanged = renderedPolicyByElement.get(element) === binding.arg;
        if (binding.value === binding.oldValue && policyUnchanged) return;
        renderSafeHtml(element, binding.value, binding.arg);
    },
    unmounted(element) {
        renderedPolicyByElement.delete(element);
    },
};
